import React, { useState } from 'react';
import {
  Box, Paper, Typography, Button, Stack, Alert, LinearProgress, TextField, IconButton, Collapse,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import axios from 'axios';
import { apiUrl, handleApiError } from './utils/api';
import NoMatchingRecords from './components/NoMatchingRecords';
import { formatDate } from './utils/date';

const money = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '';
};
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : '';
};
const sumBy = (list, key) => list.reduce((s, t) => s + (Number(t[key]) || 0), 0);

/**
 * Weekly Reconciliation (Meta's weekly P&L). For each week in the range:
 *   carrier revenue (dynamic columns) → Initial total − Driver pay → Adjusted total − Expense → Final payable.
 * Expense is editable per week and persisted.
 */
export default function WeeklyRecon() {
  const monday = (() => {
    const d = new Date();
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  })();
  const iso = (d) => d.toISOString().split('T')[0];

  const defaultStart = iso(new Date(monday.getFullYear(), monday.getMonth(), 1));
  const defaultEnd = iso(monday);
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [data, setData] = useState(null);
  const [expenseEdits, setExpenseEdits] = useState({}); // weekStart -> string
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Per-week drill-down: the ticket line items behind a week, fetched lazily from the Ticket Report
  // endpoint for that week's Mon–Sun range and grouped by day.
  const [expanded, setExpanded] = useState(() => new Set()); // weekStart values that are open
  const [weekTickets, setWeekTickets] = useState({});        // weekStart -> ticket rows[]
  const [weekBusy, setWeekBusy] = useState(() => new Set()); // weekStart values currently loading

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${apiUrl}/api/weekly-recon`, { params: { startDate, endDate } });
      setData(res.data);
      setExpenseEdits({});
    } catch (err) {
      setError(handleApiError(err, 'Failed to load weekly reconciliation'));
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setStartDate(defaultStart);
    setEndDate(defaultEnd);
    setData(null);
    setExpenseEdits({});
    setExpanded(new Set());
    setWeekTickets({});
    setError('');
  };

  // Expand/collapse a week; on first expand, fetch that week's tickets (reuses the Ticket Report endpoint,
  // whose per-ticket Initial/Driver numbers tie out to this week's Initial/Driver totals).
  const toggleWeek = async (r) => {
    const key = r.startDate;
    const next = new Set(expanded);
    if (next.has(key)) { next.delete(key); setExpanded(next); return; }
    next.add(key);
    setExpanded(next);
    if (weekTickets[key]) return; // already loaded
    setWeekBusy((b) => new Set(b).add(key));
    try {
      const res = await axios.get(`${apiUrl}/api/ticket-report`, {
        params: { startDate: r.startDate, endDate: r.endDate, basis: 'FS_CODE' },
      });
      setWeekTickets((m) => ({ ...m, [key]: Array.isArray(res.data) ? res.data : [] }));
    } catch (err) {
      setError(handleApiError(err, 'Failed to load week detail'));
    } finally {
      setWeekBusy((b) => { const n = new Set(b); n.delete(key); return n; });
    }
  };

  // Group a week's tickets by day, preserving the backend's date-ascending order.
  const groupByDay = (list) => {
    const map = new Map();
    (list || []).forEach((t) => {
      const d = t.date || '';
      if (!map.has(d)) map.set(d, []);
      map.get(d).push(t);
    });
    return [...map.entries()];
  };

  const saveExpense = async (row) => {
    const raw = expenseEdits[row.startDate];
    if (raw == null) return; // not edited
    try {
      await axios.post(`${apiUrl}/api/weekly-recon/expense`, {
        weekStart: row.startDate,
        weekEnd: row.endDate,
        amount: raw === '' ? '0' : raw,
      });
      await load(); // refetch so Adjusted/Final recompute on the server
    } catch (err) {
      setError(handleApiError(err, 'Failed to save expense'));
    }
  };

  const carriers = data?.carriers || [];
  const rows = data?.rows || [];
  const totalCol = (key) => rows.reduce((s, r) => s + (Number(r[key]) || 0), 0);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1280, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom>Weekly Reconciliation</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Carrier revenue − driver pay − expense = final payable to Meta. One row per week (Mon–Sun). Expense is editable per week.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField label="Start Date" type="date" value={startDate}
            onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
          <TextField label="End Date" type="date" value={endDate}
            onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
          <Button variant="contained" onClick={load} disabled={loading}>Generate</Button>
          <Button variant="outlined" onClick={clearFilters} disabled={loading}>Clear</Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {loading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>Loading…</Typography>
        </Box>
      )}

      {data && rows.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Start</TableCell>
                <TableCell>End</TableCell>
                {carriers.map((c) => <TableCell key={c} align="right">{c}</TableCell>)}
                <TableCell align="right"><strong>Initial total</strong></TableCell>
                <TableCell align="right">Driver</TableCell>
                <TableCell align="right"><strong>Adjusted Total</strong></TableCell>
                <TableCell align="right">Expense</TableCell>
                <TableCell align="right"><strong>Final payable</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <React.Fragment key={r.startDate}>
                <TableRow hover sx={{ '& > *': { borderBottom: expanded.has(r.startDate) ? 'unset' : undefined } }}>
                  <TableCell padding="checkbox">
                    <IconButton size="small" onClick={() => toggleWeek(r)}
                      title={expanded.has(r.startDate) ? 'Hide tickets' : 'Show tickets by day'}>
                      {expanded.has(r.startDate) ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                    </IconButton>
                  </TableCell>
                  <TableCell>{formatDate(r.startDate)}</TableCell>
                  <TableCell>{formatDate(r.endDate)}</TableCell>
                  {carriers.map((c) => (
                    <TableCell key={c} align="right">{r.carrierTotals?.[c] != null ? money(r.carrierTotals[c]) : '—'}</TableCell>
                  ))}
                  <TableCell align="right"><strong>{money(r.initialTotal)}</strong></TableCell>
                  <TableCell align="right">{money(r.driverTotal)}</TableCell>
                  <TableCell align="right"><strong>{money(r.adjustedTotal)}</strong></TableCell>
                  <TableCell align="right">
                    <TextField
                      type="number"
                      size="small"
                      variant="standard"
                      value={expenseEdits[r.startDate] ?? (r.expense ?? '')}
                      onChange={(e) => setExpenseEdits({ ...expenseEdits, [r.startDate]: e.target.value })}
                      onBlur={() => saveExpense(r)}
                      inputProps={{ style: { textAlign: 'right', width: 90 } }}
                    />
                  </TableCell>
                  <TableCell align="right"><strong>{money(r.finalPayable)}</strong></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={carriers.length + 8} sx={{ py: 0, borderBottom: expanded.has(r.startDate) ? undefined : 'none' }}>
                    <Collapse in={expanded.has(r.startDate)} timeout="auto" unmountOnExit>
                      <Box sx={{ my: 1 }}>
                        {weekBusy.has(r.startDate) ? (
                          <LinearProgress />
                        ) : (weekTickets[r.startDate] || []).length === 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                            No tickets in this week.
                          </Typography>
                        ) : (
                          <Table size="small" sx={{ bgcolor: 'background.paper' }}>
                            <TableHead>
                              <TableRow>
                                <TableCell>Ticket #</TableCell>
                                <TableCell>Company</TableCell>
                                <TableCell align="right">Tons</TableCell>
                                <TableCell>FS-Code</TableCell>
                                <TableCell align="right">Rate</TableCell>
                                <TableCell align="right">Initial</TableCell>
                                <TableCell align="right">After co.</TableCell>
                                <TableCell align="right">Driver</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {groupByDay(weekTickets[r.startDate]).map(([day, tickets]) => (
                                <React.Fragment key={day}>
                                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                                    <TableCell colSpan={5} sx={{ fontWeight: 600 }}>
                                      {formatDate(day)} · {tickets.length} ticket{tickets.length === 1 ? '' : 's'}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>{money(sumBy(tickets, 'initialTotal'))}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>{money(sumBy(tickets, 'totalAfterCompany'))}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>{money(sumBy(tickets, 'driverTotal'))}</TableCell>
                                  </TableRow>
                                  {tickets.map((t, i) => (
                                    <TableRow key={`${t.invoice}-${i}`} hover>
                                      <TableCell>{t.invoice}</TableCell>
                                      <TableCell>{t.company}</TableCell>
                                      <TableCell align="right">{num(t.tons)}</TableCell>
                                      <TableCell>{t.fsCode}</TableCell>
                                      <TableCell align="right">{money(t.rate)}</TableCell>
                                      <TableCell align="right">{money(t.initialTotal)}</TableCell>
                                      <TableCell align="right">{money(t.totalAfterCompany)}</TableCell>
                                      <TableCell align="right">{t.driverTotal != null ? money(t.driverTotal) : '—'}</TableCell>
                                    </TableRow>
                                  ))}
                                </React.Fragment>
                              ))}
                              <TableRow>
                                <TableCell colSpan={5} align="right"><strong>Week total</strong></TableCell>
                                <TableCell align="right"><strong>{money(sumBy(weekTickets[r.startDate], 'initialTotal'))}</strong></TableCell>
                                <TableCell align="right"><strong>{money(sumBy(weekTickets[r.startDate], 'totalAfterCompany'))}</strong></TableCell>
                                <TableCell align="right"><strong>{money(sumBy(weekTickets[r.startDate], 'driverTotal'))}</strong></TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
                </React.Fragment>
              ))}
              {rows.length > 0 && (
                <TableRow>
                  <TableCell colSpan={3}><strong>Totals</strong></TableCell>
                  {carriers.map((c) => (
                    <TableCell key={c} align="right">
                      {money(rows.reduce((s, r) => s + (Number(r.carrierTotals?.[c]) || 0), 0))}
                    </TableCell>
                  ))}
                  <TableCell align="right"><strong>{money(totalCol('initialTotal'))}</strong></TableCell>
                  <TableCell align="right">{money(totalCol('driverTotal'))}</TableCell>
                  <TableCell align="right"><strong>{money(totalCol('adjustedTotal'))}</strong></TableCell>
                  <TableCell align="right">{money(totalCol('expense'))}</TableCell>
                  <TableCell align="right"><strong>{money(totalCol('finalPayable'))}</strong></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {!loading && data && rows.length === 0 && <NoMatchingRecords sx={{ mt: 2 }} />}
    </Box>
  );
}
